// ---------------------------------------------------------------------------
// Restaurant search
// ---------------------------------------------------------------------------
// Powers the search box on the home page. Every restaurant now has a real
// geocoded lat/lng (set at submission/edit time — see functions/index.js
// geocodeAddress) plus structured city/country fields, so search no longer
// needs to parse US-specific address formats (state abbreviations, compass
// directions, 5-digit ZIPs) out of free text — it just matches plain text
// and food-keyword-to-cuisine, worldwide. Distance-based "Nearby" ranking is
// handled by HomePage.jsx (geocodeSearchQuery resolves any free-text
// location, in any country's format).
// ---------------------------------------------------------------------------

import { CUISINE_KEYWORDS } from '../data/cuisineKeywords';

// Lowercase, strip punctuation, collapse whitespace. Used everywhere so that
// "Washington, D.C." and "washington dc" compare equal.
export function normalize(str) {
  return (str || '')
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// Distance in miles between two {lat, lng} points (Haversine formula).
export function haversine(a, b) {
  const R = 3958.8; // Earth radius in miles
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((a.lat * Math.PI) / 180) *
      Math.cos((b.lat * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

export function coordinatesFor(value) {
  if (value?.lat == null || value?.lng == null || value.lat === '' || value.lng === '') return null;
  const lat = Number(value?.lat);
  const lng = Number(value?.lng);
  return Number.isFinite(lat) && lat >= -90 && lat <= 90 &&
    Number.isFinite(lng) && lng >= -180 && lng <= 180
    ? { lat, lng }
    : null;
}

// Does a typed food (e.g. "ramen") map to this restaurant's cuisine?
function cuisineKeywordMatch(q, cuisine) {
  const c = normalize(cuisine);
  for (const [food, cuisines] of Object.entries(CUISINE_KEYWORDS)) {
    if (q.includes(food) && cuisines.some(type => c.includes(type))) {
      return true;
    }
  }
  return false;
}

// The fields a typed word is allowed to match against.
function searchableFields(restaurant) {
  return [
    restaurant.name,
    restaurant.cuisine,
    restaurant.city,
    restaurant.state,
    restaurant.zipCode,
    restaurant.country,
    restaurant.address,
  ].map(normalize);
}

// ---------------------------------------------------------------------------
// Matching
// ---------------------------------------------------------------------------
// Two rules, in this order:
//
//   1. Contains. Every word typed has to appear *somewhere inside* one of the
//      fields — not at a word boundary. Searching "bbq" therefore finds
//      "Charles' BBQ", "BBQ Becky" and "PooBBQx's" alike, which is the whole
//      point: a visitor typing three letters is naming a thing, not spelling a
//      restaurant's name.
//
//   2. Near-miss. Only if rule 1 found nothing at all, the same words are
//      re-run allowing a small number of typos. Spelling "Vietnamese" or
//      "Mediterranean" correctly is not something a hungry person on a phone
//      should have to do to see any results.
//
// Ordering them this way is also what keeps this fast. The typo pass is the
// expensive one, and it only ever runs on a query that already returned
// nothing — so ordinary typing never pays for it.
// ---------------------------------------------------------------------------

// Minimum edit distance between `pattern` and any substring of `text`.
//
// This is the Sellers variant of Levenshtein: the first row is all zeros, so
// the pattern may start anywhere in the text at no cost, and the answer is the
// smallest value in the final row, so it may end anywhere too. That makes it
// the fuzzy counterpart of `String.includes` rather than of `===` — "italain"
// scores 1 against "modern italian kitchen" without the length of the rest of
// the field counting against it.
function approximateSubstringDistance(text, pattern, cap) {
  const m = pattern.length;
  const n = text.length;
  if (m === 0) return 0;
  if (n === 0) return m;

  let prev = new Array(n + 1).fill(0);
  let curr = new Array(n + 1);

  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    let rowBest = i;
    for (let j = 1; j <= n; j++) {
      const cost = pattern[i - 1] === text[j - 1] ? 0 : 1;
      const v = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
      curr[j] = v;
      if (v < rowBest) rowBest = v;
    }
    // Later rows can only add to the best score seen so far, so once a whole
    // row is over budget the pattern cannot fit into this text at all.
    if (rowBest > cap) return cap + 1;
    const swap = prev; prev = curr; curr = swap;
  }

  let best = prev[0];
  for (let j = 1; j <= n; j++) if (prev[j] < best) best = prev[j];
  return best;
}

// How many typos a word of this length is forgiven.
//
// Nothing under four characters gets any slack: at that length one wrong
// letter is usually a different word ("bbq"/"bar", "thai"/"chai"), and
// forgiving it would flood the results rather than rescue them.
function typoBudget(word) {
  if (word.length < 4) return 0;
  if (word.length <= 6) return 1;
  return 2;
}

function matchesWordExactly(fields, word, cuisine) {
  if (fields.some(f => f.includes(word))) return true;
  return cuisineKeywordMatch(word, cuisine);
}

function matchesWordLoosely(fields, word, cuisine) {
  if (matchesWordExactly(fields, word, cuisine)) return true;
  const budget = typoBudget(word);
  if (budget === 0) return false;
  return fields.some(f => f && approximateSubstringDistance(f, word, budget) <= budget);
}

// ---------------------------------------------------------------------------
// Main entry point used by the home page for text / food / location search.
// Distance-based ranking (via geocodeSearchQuery) is handled by HomePage.jsx.
// ---------------------------------------------------------------------------
export function searchRestaurants(restaurants, rawSearch) {
  const words = normalize(rawSearch).split(' ').filter(Boolean);
  if (!words.length) return restaurants;

  // Every word has to match something, so "bbq becky" narrows rather than
  // widens — a second word the visitor typed is a further constraint, not an
  // alternative.
  const prepared = restaurants.map(r => ({ restaurant: r, fields: searchableFields(r) }));

  const exact = prepared.filter(({ restaurant, fields }) =>
    words.every(w => matchesWordExactly(fields, w, restaurant.cuisine))
  );
  if (exact.length) return exact.map(({ restaurant }) => restaurant);

  return prepared
    .filter(({ restaurant, fields }) =>
      words.every(w => matchesWordLoosely(fields, w, restaurant.cuisine))
    )
    .map(({ restaurant }) => restaurant);
}
